import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import { Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import ModalFail from '../../../components/Shared/modal/fail';
import { ROUTER } from '../../../constants/router';

export default function Header({ cardData, riskId }) {
    const { t, i18n } = useTranslation();
    const [openFail, setOpenFail] = useState(false);

    const _getTagStatus = (e) => {
        switch (e) {
            case 'Draft':
                return { color: '#65707A', borderColor: '#65707A' };
            case 'Waiting for Review':
                return { color: '#F9C153', borderColor: '#F9C153' };
            case 'Reviewed':
                return { color: '#4ACE8F', borderColor: '#4ACE8F' };
            case 'Revise':
                return { color: '#F0536F', borderColor: '#F0536F' };
            case 'Publish':
                return { color: '#FFFFFF', backgroundColor: '#7B67FB' };
            default:
                return { color: '#FFF', background: '' };
        }
    };

    return (
        <div className="bg-white rounded-xl p-4 py-6 my-4 col-span-2 transform duration-300 hover:scale-[1.01] border-hover">
            <div className="pb-2" style={{ borderBottom: '3px solid #F4F6FC' }}>
                <div className="flex space-x-2 items-center">
                    <div className="text-xl font-[700]">
                        {t('RR.OrganizationRiskReport')}
                    </div>
                    <Tooltip
                        placement="right"
                        overlayClassName="min-w-[320px]"
                        title={
                            <div className="whitespace-pre-line">
                                {t('RR.HeaderTootip')}
                            </div>
                        }
                    >
                        <button className="bg-purple-light p-2 flex items-center justify-center rounded-lg text-purple">
                            <ExclamationCircleOutlined />
                        </button>
                    </Tooltip>
                </div>
            </div>
            {!cardData ? (
                <div className="text-center pt-8 flex flex-col items-center justify-center w-full">
                    <div className="relative w-max cursor-pointer group">
                        <svg
                            width="64"
                            height="63"
                            viewBox="0 0 64 63"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                opacity="0.1"
                                cx="32"
                                cy="31.5"
                                r="31.5"
                                fill="#A597FF"
                            />
                            <circle
                                opacity="0.2"
                                cx="32"
                                cy="31.5"
                                r="23.625"
                                fill="#A597FF"
                            />
                            <circle
                                cx="31.7951"
                                cy="31.4997"
                                r="17.1818"
                                fill="#A597FF"
                            />
                            <g clipPath="url(#clip0_6897_326662)">
                                <path
                                    d="M31.5 28.875C31.9144 28.875 32.3118 29.0396 32.6049 29.3326C32.8979 29.6257 33.0625 30.0231 33.0625 30.4375V39.8125C33.0625 40.2269 32.8979 40.6243 32.6049 40.9174C32.3118 41.2104 31.9144 41.375 31.5 41.375C31.0856 41.375 30.6882 41.2104 30.3951 40.9174C30.1021 40.6243 29.9375 40.2269 29.9375 39.8125V30.4375C29.9375 30.0231 30.1021 29.6257 30.3951 29.3326C30.6882 29.0396 31.0856 28.875 31.5 28.875ZM33.8438 24.9688C33.8438 25.5904 33.5968 26.1865 33.1573 26.626C32.7177 27.0656 32.1216 27.3125 31.5 27.3125C30.8784 27.3125 30.2823 27.0656 29.8427 26.626C29.4032 26.1865 29.1563 25.5904 29.1562 24.9688C29.1562 24.3471 29.4032 23.751 29.8427 23.3115C30.2823 22.8719 30.8784 22.625 31.5 22.625C32.1216 22.625 32.7177 22.8719 33.1573 23.3115C33.5968 23.751 33.8438 24.3471 33.8438 24.9688Z"
                                    fill="white"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_6897_326662">
                                    <rect
                                        width="25"
                                        height="25"
                                        fill="white"
                                        transform="translate(19 19.5)"
                                    />
                                </clipPath>
                            </defs>
                        </svg>
                        <div
                            className={classnames(
                                'w-[63px] h-[63px] bg-[#A597FF] opacity-10 rounded-full absolute top-0 group-hover:animate-ping'
                            )}
                        ></div>
                    </div>
                    <p className="text-[#65707A] font-normal text-sm">
                        {t('RR.RANotAvailable')}
                    </p>
                </div>
            ) : (
                <Link
                    className="text-black"
                    to={`${ROUTER.RISK_MANAGEMENT.RR}/risk-assessment/${riskId}`}
                >
                    <div className="w-full p-4">
                        <div className="grid grid-cols-2 mt-2">
                            <div className="space-y-2">
                                <div className="grid grid-cols-2 text-xs">
                                    <strong className="text-xs">
                                        {t('RList.ReviewBy')} :
                                    </strong>
                                    <div>
                                        {cardData?.reviewerFullName || '-'}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 text-xs">
                                    <strong className="text-xs">
                                        {t('RList.Version')} :
                                    </strong>
                                    <div>{cardData?.version || '-'}</div>
                                </div>
                                <div className="grid grid-cols-2 text-xs">
                                    <strong className="text-xs">
                                        {t('RList.Status')} :{' '}
                                    </strong>
                                    <div>
                                        {cardData?.statusEn ? (
                                            <Tag
                                                className="rounded-xl -my-1 text-center border-none"
                                                style={_getTagStatus(
                                                    cardData?.statusEn
                                                )}
                                            >
                                                {i18n?.language === 'en'
                                                    ? cardData?.statusEn || '-'
                                                    : cardData?.statusTh || '-'}
                                            </Tag>
                                        ) : (
                                            <>-</>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="grid grid-cols-2 text-xs">
                                    <strong className="text-xs">
                                        {t('RList.PublishDate')} :
                                    </strong>
                                    <div>{cardData?.publicDate || '-'}</div>
                                </div>
                                <div className="grid grid-cols-2 text-xs">
                                    <strong className="text-xs">
                                        {t('RList.LastUpdate')} :
                                    </strong>
                                    <div>{cardData?.modifyDate || '-'}</div>
                                </div>
                                <div className="grid grid-cols-2 text-xs">
                                    <strong className="text-xs">
                                        {t('RList.UpdateBy')} :{' '}
                                    </strong>
                                    <div>
                                        {cardData?.modifyByFullName || '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Link>
            )}
            <ModalFail
                open={openFail}
                handelClose={() => {
                    setOpenFail(false);
                }}
                handelOk={() => {
                    setOpenFail(false);
                }}
                text={<p className="lowercase">{t('RA.CreateFail')}</p>}
            />
        </div>
    );
}
